import { type JSX, useState } from 'react';

import type { Engine } from '@runtime/engine.js';
import type { FeatureFlag } from '@schema/features.js';
import type { Profile } from '@schema/profile.js';
import type { Tile } from '@schema/tiles.js';

import { ActionGrid, type ActionGridItem } from './ActionGrid.js';
import { CalculatorIcon, DeviceIcon, NetworkIcon, SpeedIcon } from './icons.js';
import { LauncherModal } from './LauncherModal.js';

export interface ToolsModalProps {
  onClose: () => void;
  onOpenCalculator: () => void;
  onOpenDnsPing: () => void;
  onOpenDeviceInfo: () => void;
  onOpenSpeedTest: () => void;
  /** Current profile — read for `tools[]` (Pro+ custom-tool tiles). */
  profile: Profile;
  /** Live feature set — gates rendering of `profile.tools`. */
  features: ReadonlySet<FeatureFlag>;
  /** Engine reference — needed to dispatch custom-tool tile activations
   *  through the same agent host as the launcher grid. */
  engine?: Engine;
  /** When set, footer shows a "← Back" link that pops one modal off
   *  the navigation stack instead of dismissing entirely. */
  onBack?: () => void;
}

/**
 * Operator-facing tools modal. Always shows three universal built-ins
 * (Device Info, Network Check, Calculator). When the `customTools`
 * feature flag is entitled AND `profile.tools` is non-empty, the
 * customer's configured tool tiles render below the built-ins,
 * dispatched through the same engine path as the main launcher grid.
 *
 * Free tier: just the built-ins.
 * Pro+ tier with no tools set: just the built-ins.
 * Pro+ tier with tools configured: built-ins + custom block.
 */
export function ToolsModal({
  onClose,
  onOpenCalculator,
  onOpenDnsPing,
  onOpenDeviceInfo,
  onOpenSpeedTest,
  profile,
  features,
  engine,
  onBack,
}: ToolsModalProps): JSX.Element {
  /** Per-tile transient error from a failed custom-tool dispatch. */
  const [toolError, setToolError] = useState<string | null>(null);

  const builtIns: ActionGridItem[] = [
    {
      id: 'device-info',
      label: 'Device Info',
      icon: <DeviceIcon />,
      onActivate: onOpenDeviceInfo,
    },
    {
      id: 'dns-ping',
      label: 'Network Check',
      icon: <NetworkIcon />,
      onActivate: onOpenDnsPing,
    },
    {
      id: 'calculator',
      label: 'Calculator',
      icon: <CalculatorIcon />,
      onActivate: onOpenCalculator,
    },
    {
      id: 'speed-test',
      label: 'Speed Test',
      icon: <SpeedIcon />,
      onActivate: onOpenSpeedTest,
    },
  ];

  // Render custom tools whenever the feature is entitled AND the
  // profile has any. The engine is only required for *dispatch* (the
  // click handler no-ops when missing) — the preview iframe runs
  // without an engine but still needs to render the tools so customers
  // can see what their Tools modal will look like at-a-glance.
  const showCustomTools = features.has('customTools') && profile.tools.length > 0;

  function dispatchCustomTool(tile: Tile): void {
    setToolError(null);
    if (!engine) return;
    void engine.dispatchTile(tile).then((result) => {
      if (!result.ok) {
        const name = tile.type === 'spacer' ? '(spacer)' : tile.label;
        setToolError(`${name}: ${describeFailure(result)}`);
        return;
      }
      // Successful tool dispatch: close the modal so the user is
      // taken to whatever the tile launched (app, URL, agent action
      // toast). Matches the launcher-grid behavior.
      onClose();
    });
  }

  return (
    <LauncherModal
      title="Tools"
      onClose={onClose}
      testId="tools-modal"
      {...(onBack && { onBack })}
    >
      <ActionGrid items={builtIns} />

      {showCustomTools && (
        <>
          <div className="tools-modal__divider" role="presentation" />
          <ActionGrid
            items={profile.tools.map((tile) => customTileToItem(tile, dispatchCustomTool))}
          />
        </>
      )}

      {toolError !== null && (
        <p className="tools-modal__err" role="alert">{toolError}</p>
      )}
    </LauncherModal>
  );
}

function customTileToItem(
  tile: Tile,
  dispatch: (tile: Tile) => void,
): ActionGridItem {
  if (tile.type === 'spacer' || tile.type === 'divider') {
    // Divider doesn't belong in the Tools modal (schema rejects it at
    // parse), but the Tile union allows it structurally — render a
    // blank action so a stray entry doesn't crash the modal.
    return {
      id: tile.id,
      label: ' ',
      icon: <span aria-hidden />,
      onActivate: () => {
        /* no-op */
      },
    };
  }
  return {
    id: tile.id,
    label: tile.label,
    icon: <ToolDefaultIcon />,
    onActivate: () => dispatch(tile),
  };
}

function describeFailure(result: { ok: false } & Record<string, unknown>): string {
  const reason = result['reason'];
  if (reason === 'notEntitled') return `feature locked (${String(result['flag'])})`;
  if (reason === 'agentError') return String(result['message']);
  if (reason === 'unsupportedTileType') return `unsupported (${String(result['type'])})`;
  return 'failed';
}

/** Neutral icon used for custom Tools tiles. Customers can override
 *  per-tile via the existing Tile.iconRef field; that surface isn't
 *  wired through ActionGrid yet, so for v1 every custom tool gets
 *  the same generic mark. */
function ToolDefaultIcon(): JSX.Element {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}
